import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Bot, Plus, Send, Trash2, Sparkles, MessageSquare, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    if (same) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
}

export default function AssistenteIAPage() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const scrollRef = useRef(null);

  async function loadSessions() {
    try {
      const r = await api.get("/ai/sessions");
      setSessions(r.data || []);
      if (!activeId && r.data && r.data.length > 0) {
        selectSession(r.data[0].id);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function selectSession(sid) {
    setActiveId(sid);
    try {
      const r = await api.get(`/ai/sessions/${sid}/messages`);
      setMessages(r.data.messages || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function newSession() {
    try {
      const r = await api.post("/ai/sessions", { title: "Nova conversa" });
      await loadSessions();
      setActiveId(r.data.id);
      setMessages([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function deleteSession(sid) {
    if (!(await confirm({ description: "Excluir esta conversa?" }))) return;
    try {
      await api.delete(`/ai/sessions/${sid}`);
      if (sid === activeId) { setActiveId(null); setMessages([]); }
      loadSessions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    // optimistic
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, tempUser]);
    setInput("");
    try {
      const r = await api.post("/ai/chat", { session_id: activeId, message: text });
      setActiveId(r.data.session_id);
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUser.id);
        return [...filtered, r.data.user_message, r.data.assistant_message];
      });
      loadSessions();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tempUser.id));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    api.get("/ai/config").then((r) => setConfig(r.data)).catch(() => {});
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  function onKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  const SAMPLE = [
    "Qual foi o faturamento do mês passado?",
    "Quantos casos foram suspensos em abril e por quê?",
    "Quais procedimentos são mais realizados pela equipe?",
    "Como funciona o rateio por turno no financeiro?",
  ];

  return (
    <div className="space-y-5" data-testid="assistente-ia-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-blue-700" /> Assistente IA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Chat baseado em {config?.model ? <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{config.model}</code> : "Claude"} — perguntas da operação, análise de documentos e divergências.
          </p>
        </div>
        {config && (
          <Badge variant="outline" className="font-mono text-[11px]">
            {config.provider} · {config.key_mode === "emergent" ? "Chave Emergent" : "chave própria"}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Sessions list */}
        <Card className="border-slate-200">
          <CardContent className="p-3 space-y-2">
            <Button size="sm" className="w-full bg-blue-700 hover:bg-blue-800" onClick={newSession} data-testid="btn-new-chat">
              <Plus className="h-4 w-4 mr-1" /> Nova conversa
            </Button>
            <div className="space-y-1 mt-2 max-h-[70vh] overflow-y-auto">
              {sessions.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-4">Nenhuma conversa ainda.</div>
              )}
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className={`group flex items-start gap-2 rounded-md px-2 py-2 cursor-pointer text-sm ${activeId === s.id ? "bg-blue-50 text-blue-900" : "hover:bg-slate-50 text-slate-700"}`}
                  onClick={() => selectSession(s.id)}
                  data-testid={`chat-session-${s.id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{s.title}</div>
                    <div className="text-[10px] text-slate-400">{fmtWhen(s.last_message_at)} · {s.message_count || 0} msg</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                    title="Excluir"
                    data-testid={`btn-delete-session-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Chat pane */}
        <Card className="border-slate-200 flex flex-col min-h-[70vh]">
          <CardContent className="p-0 flex flex-col flex-1">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 py-12">
                  <Bot className="h-12 w-12 mb-3 text-blue-600/40" />
                  <p className="text-sm font-medium text-slate-600">Como posso ajudar?</p>
                  <p className="text-xs mt-1">Pergunte sobre produção, faturamento, casos ou envie um documento para análise.</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-lg">
                    {SAMPLE.map((s) => (
                      <button
                        key={s}
                        onClick={() => setInput(s)}
                        className="text-xs border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-full px-3 py-1.5 text-slate-600 transition-colors"
                        data-testid={`sample-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-700"}`}>
                    {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-900"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                  <div className="bg-slate-100 text-slate-500 rounded-2xl px-4 py-2.5 text-sm italic">pensando…</div>
                </div>
              )}
            </div>
            <div className="border-t border-slate-200 p-3">
              <div className="flex gap-2 items-end">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Digite sua mensagem (Enter para enviar, Shift+Enter para nova linha)"
                  className="flex-1 resize-none min-h-[44px] max-h-48"
                  data-testid="chat-input"
                  disabled={sending}
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="btn-send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
